export type BackendFunctionRef = Readonly<{ path: string }>;

function ref(path: string): BackendFunctionRef {
  return Object.freeze({ path });
}

export const backendApi = {
  tenancy: {
    seedAdmin: ref('tenancy:seedAdmin'),
    getSessionContext: ref('tenancy:getSessionContext'),
    listCustomers: ref('tenancy:listCustomers'),
    listCustomersNav: ref('tenancy:listCustomersNav'),
    saveCustomer: ref('tenancy:saveCustomer'),
    createCustomerUser: ref('tenancy:createCustomerUser'),
    saveCustomerUserAccess: ref('tenancy:saveCustomerUserAccess'),
    setCustomerUserStatus: ref('tenancy:setCustomerUserStatus'),
    resetCustomerUserPassword: ref('tenancy:resetCustomerUserPassword'),
    listStaffUsers: ref('tenancy:listStaffUsers'),
    createStaffUser: ref('tenancy:createStaffUser'),
    setStaffUserStatus: ref('tenancy:setStaffUserStatus'),
    resetStaffUserPassword: ref('tenancy:resetStaffUserPassword'),
    deleteStaffUser: ref('tenancy:deleteStaffUser'),
    setActiveCustomer: ref('tenancy:setActiveCustomer'),
  },
  pbx: {
    apiRequest: ref('pbx:apiRequest'),
  },
  provisioning: {
    extensionsList: ref('provisioning:extensionsList'),
    extensionsCreate: ref('provisioning:extensionsCreate'),
    extensionsUpdate: ref('provisioning:extensionsUpdate'),
    extensionsDelete: ref('provisioning:extensionsDelete'),
    extensionsSync: ref('provisioning:extensionsSync'),
    extensionsCredentials: ref('provisioning:extensionsCredentials'),
    extensionsLastChanges: ref('provisioning:extensionsLastChanges'),
    trunksList: ref('provisioning:trunksList'),
    trunksCreate: ref('provisioning:trunksCreate'),
    trunksSetEnabled: ref('provisioning:trunksSetEnabled'),
    ivrsList: ref('provisioning:ivrsList'),
    ivrsCreate: ref('provisioning:ivrsCreate'),
    routesList: ref('provisioning:routesList'),
    routesSet: ref('provisioning:routesSet'),
    phonesList: ref('provisioning:phonesList'),
    phonesProvision: ref('provisioning:phonesProvision'),
    phonesReprovisionAll: ref('provisioning:phonesReprovisionAll'),
    systemInfo: ref('provisioning:systemInfo'),
    systemCounts: ref('provisioning:systemCounts'),
  },
  audit: {
    list: ref('audit:list'),
  },
  chatSessions: {
    verifyAccess: ref('chatSessions:verifyAccess'),
    getChatContext: ref('chatSessions:getChatContext'),
    list: ref('chatSessions:list'),
    get: ref('chatSessions:get'),
    save: ref('chatSessions:save'),
  },
  callOutcomes: {
    summarizeMissed: ref('callOutcomes:summarizeMissed'),
    listMissed: ref('callOutcomes:listMissed'),
    explainOutcome: ref('callOutcomes:explainOutcome'),
    getIngestStatus: ref('callOutcomes:getIngestStatus'),
    syncNow: ref('callOutcomes:syncNow'),
    liveMissedSummary: ref('callOutcomes:liveMissedSummary'),
    summarizeFailover: ref('callOutcomes:summarizeFailover'),
    listFailover: ref('callOutcomes:listFailover'),
    summarizeAgentActivity: ref('callOutcomes:summarizeAgentActivity'),
  },
  ivrPrompts: {
    transcribeGreetings: ref('ivrPrompts:transcribeGreetings'),
  },
  scheduledReports: {
    list: ref('scheduledReports:list'),
    save: ref('scheduledReports:save'),
    setEnabled: ref('scheduledReports:setEnabled'),
    remove: ref('scheduledReports:remove'),
    sendNow: ref('scheduledReports:sendNow'),
  },
  cms: {
    getImportStatus: ref('cms:getImportStatus'),
    startImportNow: ref('cms:startImportNow'),
  },
  crm: {
    listContacts: ref('crm:listContacts'),
    listTags: ref('crm:listTags'),
    getContact: ref('crm:getContact'),
    getTimeline: ref('crm:getTimeline'),
    saveContact: ref('crm:saveContact'),
    updateContactFields: ref('crm:updateContactFields'),
    deleteContact: ref('crm:deleteContact'),
    mergeContacts: ref('crm:mergeContacts'),
    listNewCallers: ref('crm:listNewCallers'),
    convertCaller: ref('crm:convertCaller'),
    acceptLookupSuggestions: ref('crm:acceptLookupSuggestions'),
    setCallerStatus: ref('crm:setCallerStatus'),
    ignoreOldCallers: ref('crm:ignoreOldCallers'),
    lookupNumbers: ref('crm:lookupNumbers'),
    getCallerCard: ref('crm:getCallerCard'),
    findContactByNumber: ref('crm:findContactByNumber'),
    listCompanies: ref('crm:listCompanies'),
    listCompaniesPage: ref('crm:listCompaniesPage'),
    getCompany: ref('crm:getCompany'),
    saveCompany: ref('crm:saveCompany'),
    deleteCompany: ref('crm:deleteCompany'),
    mergeCompanies: ref('crm:mergeCompanies'),
    listTasks: ref('crm:listTasks'),
    getCounts: ref('crm:getCounts'),
    saveTask: ref('crm:saveTask'),
    setTaskStatus: ref('crm:setTaskStatus'),
    rescheduleTask: ref('crm:rescheduleTask'),
    assignTask: ref('crm:assignTask'),
    addNote: ref('crm:addNote'),
    updateNote: ref('crm:updateNote'),
    deleteNote: ref('crm:deleteNote'),
    getRecoveryReport: ref('crm:getRecoveryReport'),
    getManagementReport: ref('crm:getManagementReport'),
    exportContacts: ref('crm:exportContacts'),
    importContacts: ref('crm:importContacts'),
    previewContactMatches: ref('crm:previewContactMatches'),
  },
  crmSettings: {
    getMyCrmContext: ref('crmSettings:getMyCrmContext'),
    saveMyPrefs: ref('crmSettings:saveMyPrefs'),
    saveSettings: ref('crmSettings:saveSettings'),
    listCustomerFeatures: ref('crmSettings:listCustomerFeatures'),
    setCustomerFeatures: ref('crmSettings:setCustomerFeatures'),
    savePushSubscription: ref('crmSettings:savePushSubscription'),
    removePushSubscription: ref('crmSettings:removePushSubscription'),
  },
  crmPbx: {
    clickToCall: ref('crmPbx:clickToCall'),
  },
  crmInsights: {
    listInsights: ref('crmInsights:listInsights'),
    actOnInsight: ref('crmInsights:actOnInsight'),
    refreshInsightsNow: ref('crmInsights:refreshInsightsNow'),
  },
  crmIntegrations: {
    sendTestWebhook: ref('crmIntegrations:sendTestWebhook'),
    getDigest: ref('crmIntegrations:getDigest'),
    sendDigestNow: ref('crmIntegrations:sendDigestNow'),
  },
  crmLookup: {
    getLookup: ref('crmLookup:getLookup'),
    queueLookups: ref('crmLookup:queueLookups'),
    requestTruecaller: ref('crmLookup:requestTruecaller'),
    getUsage: ref('crmLookup:getUsage'),
  },
  crmAi: {
    getBrief: ref('crmAi:getBrief'),
    getTranscript: ref('crmAi:getTranscript'),
    makeTaskFromActionItem: ref('crmAi:makeTaskFromActionItem'),
    retrySummary: ref('crmAi:retrySummary'),
  },
} as const;
